import { ITEM_TYPES_MAP } from "@shared/constants/enums";
import {
  EventPropertyMap,
  MembershipGroupPropertyMap,
  MembershipLevelPropertyMap,
  MembershipLocationPropertyMap,
  MembershipSeatPropertyMap,
  TicketLocationPropertyMap,
  TicketSeatPropertyMap,
  TicketTypePropertyMap,
} from "./seating-property-types";

export const ticketSeatPropertyMap: TicketSeatPropertyMap = {
  seatId: "id",
  permissionId: "tt_seat_permission_id",
  resaleId: "resale_submission_id",
  segmentId: "segment_id",
  basket: "basket",
  status: "status",
  payWhatYouCanPrice: "pay_what_you_can_price",
  resaleSubmissionStatus: "resale_submission_status",
};

export const membershipSeatPropertyMap: MembershipSeatPropertyMap = {
  seatId: "id",
  permissionId: "seat_permission_id",
  resaleId: undefined,
  segmentId: "segment_id",
};

export const ticketLocationPropertyMap: TicketLocationPropertyMap = {
  locationId: "id",
  permissionId: "tt_location_permission_id",
  capacityLimit: "capacity_limit",
  locationCapacity: "location_capacity",
  usageRemaining: "usage_remaining",
  segmentId: "segment_id",
  locationPendingResaleCount: "location_pending_resale_count",
};

export const membershipLocationPropertyMap: MembershipLocationPropertyMap = {
  locationId: "id",
  permissionId: "location_permission_id",
  capacityLimit: "capacity_limit",
  locationCapacity: "location_capacity",
  usageRemaining: "usage_remaining",
  segmentId: "segment_id",
};

export const eventPropertyMap: EventPropertyMap = {
  id: "id",
  name: "name",
  frontend_details_url: "frontend_details_url",
  region_is_allowed: "region_is_allowed",
  assigned_space: "assigned_space",
  slug: "slug",
  sold_out_segments: "sold_out_segments",
  starts_on: "starts_on",
  ends_on: "ends_on",
  timezone: "timezone",
  parent_event: "parent_event",
  low_inventory_warning_enabled: "low_inventory_warning_enabled",
  image_banner: "image_banner",
  location: "location",
  refund_policy: "refund_policy",
  description: "description",
  subtitle: "subtitle",
  restrictions: "restrictions",
  no_ticket_types: "no_ticket_types",
  renewal_frequency: undefined,
  membership_levels: undefined,
  benefit_data: undefined,
  venue: "venue",
  virtual_event_type: "virtual_event_type",
  date_time_to_be_determined: "date_time_to_be_determined",
  tickettype_set: "tickettype_set",
  membership_ticket_types: "membership_ticket_types",
  ticket_types: "ticket_types",
  attraction_event_config: "attraction_event_config",
  opens_at: "opens_at",
  is_recurring_parent: "is_recurring_parent",
  no_isolated_seats: "no_isolated_seats",
  sold_out: "sold_out",
  public_inventory_sold_out: "public_inventory_sold_out",
  inventory_sold_out: "inventory_sold_out",
  is_waitlisted: "is_waitlisted",
  external_link: "external_link",
  is_available_for_quantity_requested: "is_available_for_quantity_requested",
  current_season: undefined,
};

export const membershipGroupPropertyMap: MembershipGroupPropertyMap = {
  id: "id",
  name: "name",
  slug: "slug",
  frontend_details_url: "frontend_details_url",
  sold_out_segments: undefined,
  region_is_allowed: undefined,
  assigned_space: "assigned_space",
  starts_on: undefined,
  ends_on: undefined,
  timezone: undefined,
  parent_event: undefined,
  low_inventory_warning_enabled: undefined,
  image_banner: "image",
  location: undefined,
  refund_policy: undefined,
  description: "description",
  subtitle: undefined,
  restrictions: undefined,
  no_ticket_types: undefined,
  renewal_frequency: "renewal_frequency",
  membership_levels: "membership_levels",
  benefit_data: "benefit_data",
  venue: "venue",
  virtual_event_type: undefined,
  date_time_to_be_determined: undefined,
  ticket_types: undefined,
  tickettype_set: undefined,
  attraction_event_config: undefined,
  opens_at: undefined,
  is_recurring_parent: undefined,
  no_isolated_seats: undefined,
  sold_out: undefined,
  public_inventory_sold_out: undefined,
  inventory_sold_out: undefined,
  is_waitlisted: undefined,
  external_link: undefined,
  is_available_for_quantity_requested: undefined,
  current_season: "current_season",
  current_season_on_sale: "current_season_on_sale",
};

// Define a PropertyMap for 'ticket type'
export const ticketTypePropertyMap: TicketTypePropertyMap = {
  $countOptions: "$countOptions",
  colorFill: "colorFill",
  id: "id",
  name: "name",
  custom_starts_on: "custom_ticket_event_starts_on",
  custom_ends_on: "custom_ticket_event_ends_on",
  is_password_protected: "is_password_protected",
  description: "description",
  $count: "$count",
  price: "price",
  fees_pricing_info: "fees_pricing_info",
  sold_out: "sold_out",
  sort_order: "sort_order",
  sale_starts_on: "sale_starts_on",
  sale_ends_on: "sale_ends_on",
  low_ticket_inventory: "low_ticket_inventory",
  low_inventory_warning_enabled: "low_inventory_warning_enabled",
  seat_permission_color: "seat_permission_color",
  box_office_purchase_limit: "box_office_purchase_limit",
  bypass_sale_starts_on_box_office: "bypass_sale_starts_on_box_office",
  voucher_purchases_only: "voucher_purchases_only",
  inventory: "inventory",
  inventory_left: "inventory_left",
  stats: "stats",
  has_payment_plans: "has_payment_plans",
  payment_plans: "payment_plans",
  minimum_purchase_limit: "minimum_purchase_limit",
  purchase_limit: "purchase_limit",
  waitlist: "waitlist",
  pay_what_you_can_configuration: "pay_what_you_can_configuration",
};

// Define a PropertyMap for 'membership level'
export const membershipLevelPropertyMap: MembershipLevelPropertyMap = {
  colorFill: undefined,
  $countOptions: undefined,
  id: "id",
  name: "name",
  custom_starts_on: undefined,
  custom_ends_on: undefined,
  is_password_protected: undefined,
  description: "description",
  $count: undefined,
  price: "price",
  fees_pricing_info: "fees_pricing_info",
  sold_out: "sold_out",
  sort_order: "sort_order",
  sale_starts_on: undefined,
  sale_ends_on: undefined,
  low_ticket_inventory: undefined,
  low_inventory_warning_enabled: undefined,
  seat_permission_color: "seat_permission_color",
  box_office_purchase_limit: undefined,
  bypass_sale_starts_on_box_office: undefined,
  voucher_purchases_only: undefined,
  inventory: "inventory",
  inventory_left: undefined,
  stats: undefined,
  purchase_limit: "purchase_limit",
  has_payment_plans: undefined,
  payment_plans: undefined,
  minimum_purchase_limit: undefined,
  waitlist: undefined,
};

export const SEATING_ITEM_TYPE_MAP = {
  EVENT: {
    name: "event",
    propertyMap: eventPropertyMap,
    nestedItemType: {
      name: ITEM_TYPES_MAP.ITEM_TYPE_TICKET.id,
      propertyMap: ticketTypePropertyMap,
    },
    seatPermissionType: {
      name: "seat",
      propertyMap: ticketSeatPropertyMap,
    },
    locationPermissionType: {
      name: "location",
      propertyMap: ticketLocationPropertyMap,
    },
  },
  MEMBERSHIP: {
    name: "membership",
    propertyMap: membershipGroupPropertyMap,
    nestedItemType: {
      name: ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id,
      propertyMap: membershipLevelPropertyMap,
    },
    seatPermissionType: {
      name: "seat",
      propertyMap: membershipSeatPropertyMap,
    },
    locationPermissionType: {
      name: "location",
      propertyMap: membershipLocationPropertyMap,
    },
  },
};
